"use client"

import { useState, useEffect } from "react"
import { useNavigate } from "react-router-dom"
import { Eye, EyeOff, User, Lock, AlertCircle, CheckCircle } from "lucide-react"
import "./AdminLogin.css"

const AdminLogin = () => {
  const [credentials, setCredentials] = useState({
    email: "",
    password: "",
  })
  const [error, setError] = useState("")
  const [success, setSuccess] = useState("")
  const [loading, setLoading] = useState(false)
  const [showPassword, setShowPassword] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const navigate = useNavigate()

  // Hardcoded admin credentials
  const ADMIN_CREDENTIALS = {
    email: "imperiahoses@gmail.com",
    password: "Imperia@123"
  }

  // Check if already logged in
  useEffect(() => {
    const checkAuthStatus = () => {
      const isLoggedIn = localStorage.getItem("isAdminLoggedIn") === "true"

      if (isLoggedIn) {
        setIsAuthenticated(true)
        // Auto redirect to dashboard if already logged in
        navigate("/admin/dashboard")
      }
    }

    checkAuthStatus()
  }, [navigate])

  const handleSubmit = async (e) => {
    e.preventDefault()
    setError("")
    setSuccess("")
    setLoading(true)

    // Basic validation
    if (!credentials.email || !credentials.password) {
      setError("Please fill in all fields")
      setLoading(false)
      return
    }

    // Simulate API call delay
    await new Promise(resolve => setTimeout(resolve, 500))

    // Check credentials against hardcoded values
    if (
      credentials.email === ADMIN_CREDENTIALS.email &&
      credentials.password === ADMIN_CREDENTIALS.password
    ) {
      setSuccess("Login successful! Redirecting...")
      localStorage.setItem("isAdminLoggedIn", "true")
      window.dispatchEvent(new Event("adminLoginStatusChanged"))
      setIsAuthenticated(true)

      setTimeout(() => {
        navigate("/admin/dashboard")
      }, 1000)

    } else {
      setError("Invalid credentials. Please try again.")
    }

    setLoading(false)
  }

  const handleChange = (e) => {
    const { name, value } = e.target
    setCredentials(prev => ({
      ...prev,
      [name]: value
    }))
    // Clear errors when user starts typing
    if (error) setError("")
    if (success) setSuccess("")
  }

  // Manual logout function
  const handleLogout = () => {
    if (window.confirm("Are you sure you want to logout?")) {
      localStorage.removeItem("isAdminLoggedIn")
      setIsAuthenticated(false)
      setCredentials({ email: "", password: "" })
      setError("")
      setSuccess("Logged out successfully!")

      // Clear success message after a delay
      setTimeout(() => setSuccess(""), 3000)
    }
  }

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword)
  }

  return (
    <div className="admin-login">
      <div className="login-container">
        <div className="login-form">
          <div className="login-header">
            <div className="login-icon">
              <User size={32} />
            </div>
            <h2>Admin Login</h2>
            <p>Enter your credentials to access the admin panel</p>
          </div>

          {/* Error Message */}
          {error && (
            <div className="message error-message">
              <AlertCircle size={16} />
              <span>{error}</span>
            </div>
          )}

          {/* Success Message */}
          {success && (
            <div className="message success-message">
              <CheckCircle size={16} />
              <span>{success}</span>
            </div>
          )}

          {/* Show current auth status for debugging */}
          {isAuthenticated && (
            <div className="auth-status">
              <div className="auth-info">
                <User size={16} />
                <span>Logged in as: {ADMIN_CREDENTIALS.email}</span>
              </div>
              <button
                type="button"
                onClick={handleLogout}
                className="logout-btn"
              >
                Logout
              </button>
            </div>
          )}

          <form onSubmit={handleSubmit} className="login-form-fields">
            <div className="form-group">
              <label htmlFor="email">Email Address</label>
              <div className="input-wrapper">
                <User size={18} className="input-icon" />
                <input
                  id="email"
                  type="email"
                  name="email"
                  value={credentials.email}
                  onChange={handleChange}
                  placeholder="Enter your email"
                  required
                  disabled={loading}
                  autoComplete="email"
                />
              </div>
            </div>

            <div className="form-group">
              <label htmlFor="password">Password</label>
              <div className="input-wrapper">
                <Lock size={18} className="input-icon" />
                <input
                  id="password"
                  type={showPassword ? "text" : "password"}
                  name="password"
                  value={credentials.password}
                  onChange={handleChange}
                  placeholder="Enter your password"
                  required
                  disabled={loading}
                  autoComplete="current-password"
                />
                <button
                  type="button"
                  className="password-toggle"
                  onClick={togglePasswordVisibility}
                  disabled={loading}
                  aria-label={showPassword ? "Hide password" : "Show password"}
                >
                  {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            <button
              type="submit"
              className={`btn btn-primary ${loading ? "loading" : ""}`}
              disabled={loading}
            >
              {loading ? (
                <>
                  <div className="spinner"></div>
                  Logging in...
                </>
              ) : (
                "Login"
              )}
            </button>
          </form>

          <div className="login-footer">
            <p>
              <small>
                Secure admin access only. Contact IT support if you need assistance.
              </small>
            </p>
          </div>
        </div>
      </div>
    </div>
  )
}

export default AdminLogin